import { useRouter } from 'next/router';

import type { CrossChainFailureReason } from '@/lib/api/0x-cross-chain/types';

import { CHAIN_IDS } from '@/constants/chain';

import { useTransactionStore } from '@/store/transaction';
import type { CrossChainTransaction } from '@/store/transaction/types';
import { getTxHash } from '@/store/transaction/utils';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { ModalBadge } from '@/ui/Icons/ModalBadge';
import { XIcon } from '@/ui/Icons/XIcon';

import { getBlockExplorerTransactionURL } from '@/utils/blockExplorers';
import { ChainHash } from '@/utils/models';
import { routes } from '@/utils/routes';

import { AlertErrorGradient } from '../../Alerts/shared';

const CROSS_CHAIN_FAILURE_COPY: Record<CrossChainFailureReason, { title: string; body: string }> = {
  expired: {
    title: 'Bridge expired',
    body: "The bridge couldn't fill your trade in time. Funds will refund automatically — check Trade History for the refund transaction.",
  },
  cancelled: {
    title: 'Bridge cancelled',
    body: 'The bridge was cancelled before it could fill your trade. Funds will refund automatically.',
  },
  out_of_gas: {
    title: 'Out of gas',
    body: 'The transaction ran out of gas. Try again with a higher gas limit.',
  },
  internal: {
    title: 'Bridge error',
    body: 'The bridge hit an internal error. Try again, or contact support if it persists.',
  },
  unknown: {
    title: 'Transaction failed',
    body: 'Something went wrong with the bridge.',
  },
};

/** Renders terminal transaction failures without owning checkout state. */
export const FailedTransactionView = ({ onBack }: { onBack: () => void }) => {
  const router = useRouter();
  const transactionState = useTransactionStore.getState();
  const currentTransaction = useTransactionStore((state) => state.currentTransaction);

  const tx = currentTransaction?.tx;
  const txHash = tx
    ? (getTxHash(tx) ?? (tx.transactionType === 'intents' ? undefined : (tx.hash as ChainHash)))
    : undefined;
  const chainId = tx?.sellToken.chainId;
  const canSeeDetails = !!chainId && !!txHash;

  // Cross-chain trades have rich `failureContext.reason` that we can show.
  let title = 'Transaction Failed';
  let body =
    'There are a number of reasons transactions can fail, including network congestion, market volatility, or deviating from Matcha’s default gas and slippage settings.';
  if (tx?.transactionType === 'cross-chain' && currentTransaction) {
    const liveTx = transactionState.transactions[currentTransaction.address]?.[
      currentTransaction.chainId
    ]?.find((t) => t.hash === txHash) as CrossChainTransaction | undefined;
    const reason = liveTx?.failureContext?.reason;
    if (reason && CROSS_CHAIN_FAILURE_COPY[reason]) {
      ({ title, body } = CROSS_CHAIN_FAILURE_COPY[reason]);
    }
  }

  const handleSeeDetails = () => {
    if (!chainId || !txHash) return;
    // Solana txs don't have an in-app detail page; fall back to the explorer.
    if (chainId === CHAIN_IDS.SOLANA) {
      window.open(getBlockExplorerTransactionURL(chainId, txHash), '_blank', 'noopener,noreferrer');
      return;
    }
    router.push({ pathname: routes.TX, query: { chainId: String(chainId), txHash } });
  };

  const handleChatSupportClick = () => {
    window.open('https://help.matcha.xyz', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="relative size-full pb-4 tablet:pb-6 pt-20 px-6 flex flex-col justify-between text-center gap-6">
      <button
        type="button"
        aria-label="Dismiss failure screen"
        onClick={onBack}
        className="absolute top-4 right-4 z-20 p-2 rounded-full text-token-gray-900 hover:bg-token-gray-200 transition-colors"
      >
        <XIcon className="size-5" />
      </button>

      <div className="items-center flex flex-col gap-8">
        <AlertErrorGradient />
        <ModalBadge variant="error" className="relative z-10" />
        <h5 className={fontStyles({ variant: 'H5', class: ['relative', 'z-10'] })}>{title}</h5>
        <p
          className={fontStyles({
            variant: 'BODY_MD',
            class: ['text-token-gray-800', 'mb-10', 'relative', 'z-10'],
          })}
        >
          {body}{' '}
          <button
            className={linkStyles({ color: 'purple', font: 'BODY_MD', underline: true })}
            onClick={handleChatSupportClick}
          >
            Contact support
          </button>{' '}
          if you need additional help.
        </p>
      </div>

      <div className="flex flex-col tablet:flex-row gap-3">
        {canSeeDetails && (
          <button
            type="button"
            className={buttons({
              size: 'large',
              color: 'gray',
              shape: 'pill',
              class: 'flex-grow tablet:flex-1',
            })}
            onClick={handleSeeDetails}
          >
            See details
          </button>
        )}
        <button
          type="button"
          className={buttons({
            size: 'large',
            color: 'black',
            shape: 'pill',
            class: 'flex-grow tablet:flex-1',
          })}
          onClick={onBack}
        >
          Back
        </button>
      </div>
    </div>
  );
};
